import {Axios} from '@asocial/fe-utils'
import {createSelector, createSlice, Dispatch, PayloadAction} from '@reduxjs/toolkit'
import {AxiosResponse} from 'axios'
import {useQuery} from 'react-query'
import {useSelector} from 'react-redux'
import {getRandomNumbers} from 'shared/lib'
import {ErrorResponsesType} from 'shared/types'
import {VirtualGiftType} from '../types'

export type VirtualGiftCategoryType = {
  name: string
  virtualGifts: VirtualGiftType[]
}

export type VirtualGiftsModelType = {
  isOpen: boolean
  data: VirtualGiftCategoryType[]
  selectVirtualGift?: VirtualGiftType
  randomGifts: {
    desktop: VirtualGiftType[]
    mobile: VirtualGiftType[]
  }
}

const initialState: VirtualGiftsModelType = {
  isOpen: false,
  data: [],
  selectVirtualGift: undefined,
  randomGifts: {
    desktop: [],
    mobile: []
  }
}

export const virtualGiftsModel = createSlice({
  name: 'virtualGiftsModel',
  initialState,
  reducers: {
    virtualGiftsSetData: (state, {payload}: PayloadAction<VirtualGiftCategoryType[]>) => {
      state.data = payload
    },
    virtualGiftsOpenModal: (state) => {
      state.isOpen = true
    },
    virtualGiftsCloseModal: (state) => {
      state.isOpen = false
      state.selectVirtualGift = undefined
    },
    virtualGiftsSelectVirtualGift: (state, {payload}: PayloadAction<VirtualGiftType | undefined>) => {
      state.selectVirtualGift = payload
    },
    setRandomVirtualGifts: (state, {payload}: PayloadAction<VirtualGiftCategoryType[]>) => {
      if (state.randomGifts.desktop.length) return

      const virtualGifts = payload.reduce((prev, curr) => [...prev, ...curr.virtualGifts], [] as VirtualGiftType[])
      const virtualGiftsCheap = virtualGifts.filter((item) => item.price > 0 && item.price <= 160)
      const virtualGiftsNormal = virtualGifts.filter((item) => item.price > 160 && item.price <= 550)
      const virtualGiftsExpensive = virtualGifts.filter((item) => item.price > 550)

      // desktop 2 дорогих, 3 средних, 2 дешевых
      state.randomGifts.desktop.push(
        ...getRandomNumbers(virtualGiftsExpensive.length - 1, 2).map((item) => virtualGiftsExpensive[item]),
        ...getRandomNumbers(virtualGiftsNormal.length - 1, 3).map((item) => virtualGiftsNormal[item]),
        ...getRandomNumbers(virtualGiftsCheap.length - 1, 2).map((item) => virtualGiftsCheap[item])
      )

      // mobile  1 дорогой, 2 средних, 2 дешевых
      state.randomGifts.mobile.push(
        ...getRandomNumbers(virtualGiftsExpensive.length - 1, 1).map((item) => virtualGiftsExpensive[item]),
        ...getRandomNumbers(virtualGiftsNormal.length - 1, 2).map((item) => virtualGiftsNormal[item]),
        ...getRandomNumbers(virtualGiftsCheap.length - 1, 2).map((item) => virtualGiftsCheap[item])
      )
    },
  },
})

export const {
  virtualGiftsOpenModal,
  virtualGiftsCloseModal,
  virtualGiftsSelectVirtualGift
} = virtualGiftsModel.actions

export const GetVirtualGifts = (dispatch: Dispatch) =>
  useQuery<AxiosResponse<{data: VirtualGiftCategoryType[]}>, ErrorResponsesType>(
    'getVirtualGifts',
    () => Axios.get(`chat/virtual-gifts`),
    {
      onSuccess: ({data: response}) => {
        dispatch(virtualGiftsModel.actions.virtualGiftsSetData(response.data))
        dispatch(virtualGiftsModel.actions.setRandomVirtualGifts(response.data))
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

export const useVirtualGiftsData = () => useSelector(
  createSelector(
    (state: RootState) => state.virtualGifts.data,
    (data) => data
  )
)

export const useVirtualGiftsIsOpen = () => useSelector(
  createSelector(
    (state: RootState) => state.virtualGifts.isOpen,
    (isOpen) => isOpen
  )
)

export const useVirtualGiftsSelectVirtualGift = () => useSelector(
  createSelector(
    (state: RootState) => state.virtualGifts.selectVirtualGift,
    (selectVirtualGift) => selectVirtualGift
  )
)

export const useVirtualGiftsRandomGifts = () => useSelector(
  createSelector(
    (state: RootState) => state.virtualGifts.randomGifts,
    (randomGifts) => randomGifts
  )
)